import React, { useEffect } from 'react';
import './App.scss';
import Onboard from '../onboard/Onboard';
import Core from '../core/Core';
import { experiences } from './models/experiences';
import { useOnboardDispatch, useCoreDispatch, useCoreSelector } from 'Contexts/StoreContext';
import { AppExperienceService } from './services/AppExperienceService';
import FullScreenLoading from 'Core_Components/FullScreenLoading/FullScreenLoading';
import { ExperienceContext } from 'Contexts/ExperienceContext';
import { useAtom } from 'jotai';
import { experienceAtom } from './atoms/experienceAtom';
import { changeLanguage as onboardChangeLanguage } from 'Onboard_Redux/user';
import { changeLanguage as coreChangeLanguage } from 'Core_Redux/user';
import LinkOnboard from '../linkOnboard/LinkOnboard';
import { selectDeepLinkPath, selectBranchTokens, selectIsBranchInitialized } from 'Core_Redux/deepLink';
import { appInsights } from 'Core_Helpers/AppInsights';
import StorageService, { TOKEN_RESPONSE_KEY } from 'Core_Helpers/authentication/StorageService';
import { HOME } from 'Core_Pages/Routes/RoutesConfig';
import { useHistory } from 'react-router-dom';

const AppRenderer = () => {
  const [experience, setExperience] = useAtom(experienceAtom);
  const onboardDispatch = useOnboardDispatch();
  const coreDispatch = useCoreDispatch();
  const history = useHistory();
  const deepLinkPath = useCoreSelector(selectDeepLinkPath);
  const branchTokens = useCoreSelector(selectBranchTokens);
  const isBranchInitialized = useCoreSelector(selectIsBranchInitialized);
  const experienceService = new AppExperienceService();

  const experienceUpdatedAsync = async () => {
    setExperience(experiences.LOADING);
    const currentExp = await experienceService.determineUpdatedExperienceAsync();
    //it has proven difficult to retrieve the dispatch method with the correct state outside of react, so pass dispatch here explicitly.
    await experienceService.handleUpdateEffectsAsync(currentExp, onboardDispatch, coreDispatch);
    setExperience(currentExp);
  };

  const updateLanguage = (language) => {
    coreDispatch(coreChangeLanguage({ language }));
    onboardDispatch(onboardChangeLanguage({ language }));
  };

  const renderExperience = () => {
    switch (experience) {
      case experiences.CORE:
        return <Core />;
      case experiences.ONBOARD:
        return <Onboard />;
      case experiences.LINK_ONBOARD:
        return <LinkOnboard />;
      case experiences.LOADING:
      default:
        return (
          <div style={{ display: 'flex', margin: 'auto' }}>
            <FullScreenLoading showMessage={true} />;
          </div>
        );
    }
  };

  useEffect(() => {
    const handleBranchInitialization = async () => {
      if (isBranchInitialized) {
        if (branchTokens?.accessToken && branchTokens?.refreshToken) {
          setExperience(experiences.LOADING);
          const storage = await StorageService.getInstance();
          const oldTokens = await storage.getItem(TOKEN_RESPONSE_KEY);
          const newTokens = JSON.stringify({
            accessToken: branchTokens.accessToken,
            refreshToken: branchTokens.refreshToken,
          });
          await storage.setItem(TOKEN_RESPONSE_KEY, newTokens);
          try {
            await experienceUpdatedAsync();
            history.push(HOME);
          } catch (error) {
            appInsights.trackEvent({ name: `DEEP_LINK: AppRenderer:Error with login: ${error}` });
            await storage.setItem(TOKEN_RESPONSE_KEY, oldTokens);
            await experienceUpdatedAsync();
            history.push(HOME);
          }
        }

        if (deepLinkPath == '/link-onboard') {
          appInsights.trackEvent({ name: 'DEEP_LINK: AppRenderer:Directing to LINK_ONBOARD' });
          setExperience(experiences.LINK_ONBOARD);
        }
      }
    };
    handleBranchInitialization();
  }, [isBranchInitialized, deepLinkPath, branchTokens]);

  //use context to distribute this function as to not introduce new state management dependencies two the different app experiences
  //this should be revisited when those experiences are refactored and should leverage any new technologies, i.e. jotai, in those refactors
  return (
    <ExperienceContext.Provider value={{ experienceUpdatedAsync, updateLanguage }}>
      {renderExperience()}
    </ExperienceContext.Provider>
  );
};

export default AppRenderer;
